import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import NotificationBell from '../components/NotificationBell.jsx';
import { api } from '../lib/api.js';
import { supabase } from '../lib/supabase.js';

const TABS = [
  ['profile', 'Profile'],
  ['notifications', 'Notifications'],
  ['security', 'Security'],
];

function roleLabel(role) {
  return String(role || '').split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function Notice({ tone = 'success', children }) {
  const classes = tone === 'error' ? 'border-red-200 bg-red-50 text-red-800' : 'border-mid-green/30 bg-sage-100 text-hunter-deep';
  return <div className={`rounded-lg border px-4 py-3 text-sm font-semibold ${classes}`}>{children}</div>;
}

function Toggle({ checked, disabled, onChange, label, detail }) {
  return (
    <label className={`flex items-center justify-between gap-5 px-5 py-4 ${disabled ? 'bg-silver-a/45' : 'cursor-pointer hover:bg-silver-a/40'}`}>
      <span className="min-w-0">
        <span className="block text-sm font-bold text-silver-text">{label}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-silver-mute">{detail}{disabled ? ' This safety notification is always on.' : ''}</span>
      </span>
      <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? 'bg-mid-green' : 'bg-gray-300'} ${disabled ? 'opacity-65' : ''}`}>
        <input type="checkbox" className="sr-only" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
        <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
      </span>
    </label>
  );
}

export default function TeamMemberProfile() {
  const [tab, setTab] = useState('profile');
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({ first_name: '', last_name: '', phone: '' });
  const [preferences, setPreferences] = useState({});
  const [passwords, setPasswords] = useState({ password: '', confirm: '' });

  useEffect(() => {
    api.get('/api/auth/me').then((data) => {
      setAccount(data);
      setProfile({ first_name: data.team.first_name || '', last_name: data.team.last_name || '', phone: data.team.phone || '' });
      setPreferences(data.team.notification_preferences || {});
    }).catch((error) => setMessage({ tone: 'error', text: error.message })).finally(() => setLoading(false));
  }, []);

  const groupedOptions = useMemo(() => {
    const groups = new Map();
    for (const option of account?.notification_options || []) {
      if (!groups.has(option.group)) groups.set(option.group, []);
      groups.get(option.group).push(option);
    }
    return [...groups.entries()];
  }, [account]);

  function chooseTab(next) {
    setTab(next);
    setMessage(null);
  }

  async function saveProfile(event) {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const data = await api.patch('/api/auth/profile', profile);
      setAccount((current) => ({ ...current, team: { ...current.team, ...data.team } }));
      setMessage({ tone: 'success', text: 'Profile saved.' });
    } catch (error) {
      setMessage({ tone: 'error', text: error.message });
    } finally { setSaving(false); }
  }

  async function savePreferences() {
    setSaving(true);
    setMessage(null);
    try {
      const data = await api.patch('/api/auth/notification-preferences', { preferences });
      setPreferences(data.preferences);
      setMessage({ tone: 'success', text: 'Notification preferences saved.' });
    } catch (error) {
      setMessage({ tone: 'error', text: error.message });
    } finally { setSaving(false); }
  }

  async function changePassword(event) {
    event.preventDefault();
    setMessage(null);
    if (passwords.password.length < 8) return setMessage({ tone: 'error', text: 'Password must be at least 8 characters.' });
    if (passwords.password !== passwords.confirm) return setMessage({ tone: 'error', text: 'Passwords do not match.' });
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: passwords.password });
    setSaving(false);
    if (error) return setMessage({ tone: 'error', text: error.message });
    setPasswords({ password: '', confirm: '' });
    setMessage({ tone: 'success', text: 'Password updated.' });
  }

  return (
    <div className="min-h-screen bg-[#f5f3ed] text-silver-text">
      <header className="border-b border-white/20 bg-hunter-deep text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-7">
          <div><div className="font-display text-2xl font-black">CCF Front Office</div><div className="mt-1 text-[0.6rem] font-bold uppercase tracking-[0.25em] text-mid-green">Team Member Profile</div></div>
          <div className="flex items-center gap-4"><NotificationBell tone="dark" /><Link to="/" className="text-sm font-semibold text-silver-a hover:text-white">← Front Office</Link></div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8 sm:px-7">
        <div className="mb-7">
          <div className="text-[0.62rem] font-bold uppercase tracking-[0.3em] text-hunter">Your account</div>
          <h1 className="mt-1 font-athletic text-5xl font-black uppercase leading-none text-hunter-deep sm:text-6xl">Team Member Profile</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-silver-mute">Manage your own contact details, notification choices, and password. Team roles and permissions remain in CCF Team Management.</p>
        </div>

        {loading ? <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-silver-mute">Loading your profile…</div> : (
          <div className="space-y-5">
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="flex items-center gap-4 px-5 py-4 sm:px-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-hunter text-xl font-black text-white">{(account?.team?.first_name?.[0] || account?.team?.full_name?.[0] || 'C').toUpperCase()}</div>
                <div className="min-w-0">
                  <div className="truncate font-display text-lg font-black text-hunter-deep">{account?.team?.full_name}</div>
                  <div className="mt-1 text-xs font-bold uppercase tracking-wider text-mid-green">{roleLabel(account?.team?.role)}</div>
                </div>
              </div>
              <nav className="flex overflow-x-auto border-t border-gray-100 px-2 sm:px-4" aria-label="Profile sections" role="tablist">
                {TABS.map(([id, label]) => <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => chooseTab(id)} className={`relative shrink-0 px-4 py-3 text-sm font-bold transition-colors ${tab === id ? 'text-hunter-deep after:absolute after:inset-x-4 after:bottom-0 after:h-0.5 after:bg-mid-green' : 'text-silver-mute hover:text-hunter'}`}>{label}</button>)}
              </nav>
            </div>

            <section className="min-w-0">
              {message && <div className="mb-4"><Notice tone={message.tone}>{message.text}</Notice></div>}

              {tab === 'profile' && <form onSubmit={saveProfile} className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-6 py-5"><h2 className="font-display text-xl font-black text-hunter-deep">Profile</h2><p className="mt-1 text-sm text-silver-mute">The name and phone number other CCF team members see.</p></div>
                <div className="grid gap-5 p-6 sm:grid-cols-2">
                  <label className="block"><span className="mb-1.5 block text-sm font-bold">First name</span><input required value={profile.first_name} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} className="input" /></label>
                  <label className="block"><span className="mb-1.5 block text-sm font-bold">Last name</span><input required value={profile.last_name} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} className="input" /></label>
                  <label className="block sm:col-span-2"><span className="mb-1.5 block text-sm font-bold">Phone</span><input type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="input" placeholder="Optional" /></label>
                  <div className="sm:col-span-2"><span className="mb-1.5 block text-sm font-bold">Email</span><div className="rounded-md border border-gray-200 bg-silver-a/60 px-3 py-2 text-sm text-silver-mute">{account?.team?.email}</div><p className="mt-1.5 text-xs text-silver-mute">Ask an administrator to change the email tied to your login.</p></div>
                </div>
                <div className="flex justify-end border-t border-gray-100 bg-silver-a/35 px-6 py-4"><button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save profile'}</button></div>
              </form>}

              {tab === 'notifications' && <div className="space-y-4">
                <div className="rounded-lg border border-gray-200 bg-white px-6 py-5 shadow-sm"><h2 className="font-display text-xl font-black text-hunter-deep">Notifications</h2><p className="mt-1 text-sm leading-relaxed text-silver-mute">Choose which football and business events appear in your general notification feed. Service-delivery tasks stay in the clipboard queue.</p></div>
                {groupedOptions.map(([group, options]) => <div key={group} className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"><h3 className="border-b border-gray-100 bg-silver-a/35 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-hunter">{group}</h3><div className="divide-y divide-gray-100">{options.map((option) => <Toggle key={option.key} {...option} checked={preferences[option.key] !== false} disabled={option.locked} onChange={(enabled) => setPreferences((current) => ({ ...current, [option.key]: enabled }))} />)}</div></div>)}
                <div className="flex justify-end"><button type="button" onClick={savePreferences} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save notification preferences'}</button></div>
              </div>}

              {tab === 'security' && <form onSubmit={changePassword} className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-6 py-5"><h2 className="font-display text-xl font-black text-hunter-deep">Security</h2><p className="mt-1 text-sm text-silver-mute">Change the password for {account?.auth?.email}. No email will be sent.</p></div>
                <div className="space-y-5 p-6">
                  <label className="block"><span className="mb-1.5 block text-sm font-bold">New password</span><input required minLength={8} autoComplete="new-password" type="password" value={passwords.password} onChange={(e) => setPasswords({ ...passwords, password: e.target.value })} className="input" /><span className="mt-1.5 block text-xs text-silver-mute">At least 8 characters.</span></label>
                  <label className="block"><span className="mb-1.5 block text-sm font-bold">Confirm new password</span><input required minLength={8} autoComplete="new-password" type="password" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} className="input" /></label>
                </div>
                <div className="flex justify-end border-t border-gray-100 bg-silver-a/35 px-6 py-4"><button type="submit" disabled={saving} className="btn-primary">{saving ? 'Updating…' : 'Update password'}</button></div>
              </form>}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
